import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { usePostByTenantOrderConfirmationMutation } from '@/api/rtk-api'
import {
  Alert,
  CheckboxField,
  CurrencyField,
  LabelValueRow,
  OperationButton,
  Rule,
  ToastNotification
} from '@/components/core-v2'
import { CURRENCY, TENANT } from '@/const'
import { formatCurrencyFull } from '@/utilities/currency'
import { formatUnits } from '@/utilities/number'

import { OutstandingOrder } from './orders-types'

interface PricePerUnitProps {
  id: string
  order: OutstandingOrder
}
const PricePerUnit = ({ id, order }: PricePerUnitProps) => {
  if (order.confirmation)
    return (
      <span>{formatCurrencyFull(order.confirmation.executionPrice, CURRENCY, null, true)}</span>
    )

  if (order.state !== 'Placed') return <span>-</span>

  return (
    <CurrencyField
      decimalPlaces={4}
      id={id}
      symbol="ZAc"
      validators={{
        validate: {
          nonNegative: ({ numerical: value }) => value > 0
        }
      }}
    />
  )
}

export interface OrderOutstandingDetailsProps {
  order: OutstandingOrder
}

const manualCompletionId = 'manualCompletion'
const submitConfirmationId = 'submitConfirmation'

export const OrderOutstandingDetails = ({ order }: OrderOutstandingDetailsProps) => {
  const [showToast, setShowToast] = useState(false)
  const [mutate, { isLoading, isSuccess }] = usePostByTenantOrderConfirmationMutation()
  const form = useForm({ mode: 'onBlur', reValidateMode: 'onChange' })
  const { numerical: manualPrice } = form.watch(manualCompletionId, { numerical: undefined })
  const submitConfirmation = form.watch(submitConfirmationId)
  const onSubmit = data => {
    mutate({
      apiNewAggregatedOrderConfirmation: {
        executionPrice: data[manualCompletionId].numerical,
        orderId: order.orderId
      },
      tenant: TENANT
    })
  }

  useEffect(() => {
    if (isSuccess) {
      setShowToast(true)
    }
  }, [isSuccess])

  const { estimatedPrice } = order
  const lower = manualPrice < estimatedPrice - estimatedPrice * 0.1
  const higher = estimatedPrice + estimatedPrice * 0.1 < manualPrice
  const outsideRange = !!manualPrice && (lower || higher)

  let units
  let amount
  if (order.value) {
    if (order.confirmation) {
      units = order.value / (order.confirmation.executionPrice / 100)
    } else if (order.state === 'Placed') {
      units = order.value / (manualPrice / 100)
    } else {
      units = '-'
    }
  } else {
    units = order.shares ? Math.abs(order.shares) : '-'
  }

  if (order.shares) {
    if (order.confirmation) {
      amount = Math.abs(order.shares) * (order.confirmation.executionPrice / 100)
    } else if (order.state === 'Placed') {
      amount = manualPrice ? Math.abs(order.shares) * (manualPrice / 100) : '-'
    } else {
      amount = order.value
    }
  } else {
    amount = order.value
  }

  return (
    <FormProvider {...form}>
      <form data-testid="order-outstanding-details-form" onSubmit={form.handleSubmit(onSubmit)}>
        <LabelValueRow label="Order ID" value={order.orderId} />
        <LabelValueRow
          label="Order generated"
          value={new Date(order.createdDate).toLocaleDateString()}
        />
        <LabelValueRow
          label={`Price per unit (estimated ${formatCurrencyFull(
            order.estimatedPrice,
            CURRENCY,
            null,
            true
          )})`}
          value={<PricePerUnit id={manualCompletionId} order={order} />}
        />
        <LabelValueRow
          label="Number of units"
          value={typeof units === 'number' ? formatUnits(units) : units}
        />
        <LabelValueRow label="Number of trades" value={order.individualOrders.length} />
        <LabelValueRow label="Total amount" value={formatCurrencyFull(amount, CURRENCY)} />
        {order.state === 'Placed' && !order.confirmation && (
          <>
            <Rule />
            {outsideRange && (
              <Alert className="mx-8 mt-4" type="warning">
                Warning: this price is different from the estimated completion price by more than
                10%
              </Alert>
            )}
            <CheckboxField
              className="ml-8 py-4"
              id={submitConfirmationId}
              isDisabled={!manualPrice}
              label="Please confirm price per unit. Once set it cannot be changed."
            />
            <OperationButton
              className="ml-8 mb-4"
              isDisabled={!submitConfirmation}
              isLoading={isLoading}
              operationType="progress"
              type="submit"
            >
              Submit
            </OperationButton>
          </>
        )}
        <ToastNotification
          description="Manually submitted price per unit successfully!"
          notificationType="success"
          open={showToast}
          title="Manually completed order"
          onClose={() => setShowToast(false)}
        />
      </form>
    </FormProvider>
  )
}
